import { useEffect, useRef, useState } from "react";
import { IconPrinter, IconPlay, IconPause, IconChevronLeft, IconChevronRight, IconMegaphone, IconTimer, IconEdit, IconX } from "../../Components/Icons/Icons";
import { usePageTitle } from "../../Hooks/usePageTitle";
import { useNavigate, useParams } from "react-router-dom";
import "./AdminPage.css";
import { Project } from "../../Interfaces/Project/Project";
import { Cue } from "../../Interfaces/Cue/Cue";
import { CustomField, DEFAULT_FIELDS } from "../../Interfaces/CustomField/CustomField";
import { db, collection, getDocs, query, where, updateDoc, doc, onSnapshot, writeBatch } from "../../Backend/firebase";
import { LoadingScreen } from "../../Components/LoadingScreen/LoadingScreen";
import { PrintableCueSheet } from "../../Components/PrintableCueSheet/PrintableCueSheet";

interface AdminPageProps {
  projects: Project[];
}

function todSecs(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
}

function cueDurationSecs(cue: Cue): number {
  return Math.max(0, todSecs(cue.endTime) - todSecs(cue.startTime));
}

// Drift measured against the cue's own planned DURATION (not the scheduled
// clock time): how long the cue actually ran vs how long it was allotted.
// Positive = ran long (behind), negative = wrapped early (ahead), null until
// the cue has started. A cue actually ends when the next cue goes live; the
// still-live cue is measured against "now" and reads on-time until it overruns.
function driftMinutes(sorted: Cue[], index: number, nowMs: number): number | null {
  const cue = sorted[index];
  if (!cue.actualStartTime) return null;
  const startMs = new Date(cue.actualStartTime).getTime();
  let endMs: number | null = null;
  for (let j = index + 1; j < sorted.length; j++) {
    const later = sorted[j].actualStartTime;
    if (later) { endMs = new Date(later).getTime(); break; }
  }
  const actualSecs = ((endMs ?? nowMs) - startMs) / 1000;
  const planned = cueDurationSecs(cue);
  if (cue.isLive && actualSecs < planned) return 0; // on track — no overrun yet
  return Math.round((actualSecs - planned) / 60);
}

function mapCue(data: any, id: string): Cue {
  const fieldValues: Record<string, string> = data.fieldValues || {};
  if (!data.fieldValues) {
    ['presenter','location','avMedia','audioSource','sideScreens','centerScreen','lighting','ambientLights','notes']
      .forEach(k => { if (data[k]) fieldValues[k] = data[k]; });
  }
  return {
    id,
    cueNumber: data.cueNumber,
    title: data.title || '',
    startTime: data.startTime?.toDate ? data.startTime.toDate().toISOString() : (data.startTime || new Date().toISOString()),
    endTime: data.endTime?.toDate ? data.endTime.toDate().toISOString() : (data.endTime || new Date().toISOString()),
    projectRef: data.projectRef,
    isLive: data.isLive ?? false,
    fieldValues,
    actualStartTime: data.actualStartTime,
  };
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
}

function fmtDuration(startIso: string, endIso: string) {
  const mins = Math.round((todSecs(endIso) - todSecs(startIso)) / 60);
  if (mins <= 0) return '—';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function fmtElapsed(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function fmtCountdown(secs: number) {
  const abs = Math.abs(Math.round(secs));
  const s = abs % 60;
  const m = Math.floor(abs / 60) % 60;
  const h = Math.floor(abs / 3600);
  if (h > 0) return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function toTimeInput(iso: string) {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function applyTimeInput(baseIso: string, timeStr: string): string {
  const d = new Date(baseIso);
  const [h, m] = timeStr.split(':').map(Number);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

function AdminPage({ projects }: AdminPageProps) {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [cues, setCues] = useState<Cue[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  usePageTitle(project ? `${project.title} – Admin` : "Admin");
  const [fields, setFields] = useState<CustomField[]>(DEFAULT_FIELDS);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [now, setNow] = useState(new Date());
  const [urlCopied, setUrlCopied] = useState(false);
  const [shareEnabled, setShareEnabled] = useState(true);
  const [broadcastInput, setBroadcastInput] = useState('');
  const [broadcastSent, setBroadcastSent] = useState(false);
  const [editingCue, setEditingCue] = useState<Cue | null>(null);
  const [editForm, setEditForm] = useState<{ title: string; startTime: string; endTime: string; fieldValues: Record<string,string> }>({
    title: '', startTime: '', endTime: '', fieldValues: {},
  });
  const liveCardRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const shareUrl = `https://live-cue.com/#/LiveCueSheet/${projectId}`;

  // Initial load + project metadata
  useEffect(() => {
    if (!projectId) return;
    getDocs(query(collection(db, 'cues'), where('projectRef', '==', projectId)))
      .then(snap => {
        setCues(snap.docs.map(d => mapCue(d.data(), d.id)));
        setLoading(false);
      })
      .catch(console.error);
    const found = projects.find(p => p.firebaseID === projectId);
    if (found) {
      setProject(found);
      setFields(found.fields?.length ? found.fields : DEFAULT_FIELDS);
      setShareEnabled(found.shareEnabled !== false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projects]);

  // Real-time listener
  useEffect(() => {
    if (!projectId) return;
    const q = query(collection(db, 'cues'), where('projectRef', '==', projectId));
    return onSnapshot(q, snap => {
      setCues(snap.docs.map(d => mapCue(d.data(), d.id)));
      setLoading(false);
    });
  }, [projectId]);

  // Live clock (every second)
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Elapsed timer
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setElapsedTime(p => p + 1), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  // Auto-scroll live card to center
  useEffect(() => {
    if (liveCardRef.current && scrollRef.current) {
      const card = liveCardRef.current;
      const container = scrollRef.current;
      container.scrollTo({
        left: card.offsetLeft + card.offsetWidth / 2 - container.offsetWidth / 2,
        behavior: 'smooth',
      });
    }
  }, [cues]);

  const sorted = [...cues].sort((a, b) => a.cueNumber - b.cueNumber);
  const liveIdx = sorted.findIndex(c => c.isLive);
  const liveCue = liveIdx >= 0 ? sorted[liveIdx] : null;
  const nextCue = liveIdx >= 0 ? sorted[liveIdx + 1] : sorted[0];
  const globalDrift = liveIdx >= 0 ? driftMinutes(sorted, liveIdx, now.getTime()) : null;

  const ns = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

  const nextCueSecs = nextCue ? todSecs(nextCue.startTime) - ns : null;

  // Elapsed on the live cue measured from when it actually started (not the
  // scheduled clock), so the progress bar tracks real time-in-cue.
  const liveElapsedSecs = liveCue
    ? (liveCue.actualStartTime
        ? Math.max(0, (now.getTime() - new Date(liveCue.actualStartTime).getTime()) / 1000)
        : 0)
    : null;
  const liveDurationSecs = liveCue ? cueDurationSecs(liveCue) : null;
  const liveProgressPct = (liveElapsedSecs != null && liveDurationSecs && liveDurationSecs > 0)
    ? Math.min(100, Math.max(0, (liveElapsedSecs / liveDurationSecs) * 100))
    : 0;

  const toggleLive = async () => {
    setIsLive(true);
    setIsRunning(true);
    if (sorted[0]) {
      await updateDoc(doc(db, 'cues', sorted[0].id), { actualStartTime: new Date().toISOString() });
    }
  };

  const togglePause = () => setIsRunning(p => !p);
  const adjustTime = (s: number) => setElapsedTime(p => Math.max(0, p + s));

  // Flip both cues in a single atomic batch so the real-time listener never
  // observes an in-between state with no live cue (which flashed "No cue live").
  const handleNextCue = async () => {
    if (liveIdx === -1 || liveIdx >= sorted.length - 1) return;
    const next = sorted[liveIdx + 1];
    const batch = writeBatch(db);
    batch.update(doc(db, 'cues', sorted[liveIdx].id), { isLive: false });
    batch.update(doc(db, 'cues', next.id), { isLive: true, actualStartTime: new Date().toISOString() });
    await batch.commit();
  };

  const handlePrevCue = async () => {
    if (liveIdx <= 0) return;
    const batch = writeBatch(db);
    batch.update(doc(db, 'cues', sorted[liveIdx].id), { isLive: false });
    batch.update(doc(db, 'cues', sorted[liveIdx - 1].id), { isLive: true });
    await batch.commit();
  };

  const openEditor = (cue: Cue) => {
    setEditingCue(cue);
    setEditForm({
      title: cue.title,
      startTime: toTimeInput(cue.startTime),
      endTime: toTimeInput(cue.endTime),
      fieldValues: { ...cue.fieldValues },
    });
  };

  const saveEdit = async () => {
    if (!editingCue) return;
    await updateDoc(doc(db, 'cues', editingCue.id), {
      title: editForm.title,
      startTime: applyTimeInput(editingCue.startTime, editForm.startTime),
      endTime: applyTimeInput(editingCue.endTime, editForm.endTime),
      fieldValues: editForm.fieldValues,
    });
    setEditingCue(null);
  };

  const sendBroadcast = async () => {
    if (!broadcastInput.trim() || !projectId) return;
    await updateDoc(doc(db, 'projects', projectId), {
      broadcastMessage: broadcastInput.trim(),
      broadcastAt: new Date().toISOString(),
    });
    setBroadcastSent(true);
    setTimeout(() => setBroadcastSent(false), 2000);
  };

  const clearBroadcast = async () => {
    if (!projectId) return;
    await updateDoc(doc(db, 'projects', projectId), {
      broadcastMessage: '',
      broadcastAt: null,
    });
    setBroadcastInput('');
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  };

  const toggleShare = async () => {
    if (!projectId) return;
    const next = !shareEnabled;
    setShareEnabled(next);
    try {
      await updateDoc(doc(db, 'projects', projectId), { shareEnabled: next });
    } catch {
      setShareEnabled(!next); // revert on failure
    }
  };

  function cardVariant(index: number): 'past' | 'live' | 'next' | 'future' {
    if (liveIdx < 0) return index === 0 ? 'next' : 'future';
    const diff = index - liveIdx;
    if (diff < 0) return 'past';
    if (diff === 0) return 'live';
    if (diff === 1) return 'next';
    return 'future';
  }

  if (loading) return <LoadingScreen />;

  return (
    <div className="adm-shell">

      {/* ── Top bar ── */}
      <header className="adm-topbar">
        <div className="adm-topbar-left">
          <span className="adm-brand" onClick={() => navigate('/HomePage')}>
            <span className="adm-brand-live">LIVE</span><span className="adm-brand-cue">CUE</span>
          </span>
          <span className="adm-topbar-sep">|</span>
          <span className="adm-topbar-project">{project?.title || '—'}</span>
          {project?.date && (
            <span className="adm-topbar-date">
              {project.date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          )}
        </div>

        <div className="adm-topbar-center">
          {isLive ? (
            <div className="adm-on-air">
              <span className="adm-on-air-dot" />
              ON AIR
            </div>
          ) : (
            <div className="adm-standby">STANDBY</div>
          )}
        </div>

        <div className="adm-topbar-right">
          <button className="adm-print-btn" onClick={() => window.print()} title="Print or save as PDF" aria-label="Print or save as PDF"><IconPrinter size={17} /></button>
          {globalDrift !== null && (
            <span className={`adm-drift-badge adm-drift--${globalDrift === 0 ? 'ok' : globalDrift > 0 ? 'late' : 'early'}`}>
              {globalDrift === 0 ? '✓ ON TIME' : globalDrift > 0 ? `▲ +${globalDrift}m` : `▼ ${Math.abs(globalDrift)}m`}
            </span>
          )}
          <span className="adm-topbar-clock">
            {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
          </span>
        </div>
      </header>

      <div className="adm-body">

        {/* ── Control console ── */}
        <aside className="adm-console">

          {/* SHOW TIMER */}
          <div className="adm-module">
            <div className="adm-module-label">SHOW TIMER</div>
            <div className="adm-module-body">
              <div className={`adm-big-timer ${isRunning ? 'adm-big-timer--running' : ''}`}>
                {fmtElapsed(elapsedTime)}
              </div>
              <div className="adm-cue-progress-label">
                {liveCue ? `${liveCue.cueNumber} of ${sorted.length} — ${liveCue.title}` : 'No cue live'}
              </div>
              {liveCue && (
                <div className="adm-progress-track">
                  <div className="adm-progress-fill" style={{ width: `${liveProgressPct}%` }} />
                </div>
              )}
            </div>
          </div>

          {/* TRANSPORT */}
          <div className="adm-module">
            <div className="adm-module-label">TRANSPORT</div>
            <div className="adm-module-body">
              {!isLive ? (
                <button className="adm-go-live-btn" onClick={toggleLive}>
                  <span className="adm-go-dot" />
                  GO LIVE
                </button>
              ) : (
                <div className="adm-transport-row">
                  <button className="adm-transport-btn" onClick={() => adjustTime(-10)}>−10s</button>
                  <button className={`adm-transport-btn adm-transport-btn--play ${isRunning ? 'adm-transport-btn--pause' : ''}`} onClick={togglePause}>
                    {isRunning ? <IconPause size={18} /> : <IconPlay size={18} />}
                  </button>
                  <button className="adm-transport-btn" onClick={() => adjustTime(10)}>+10s</button>
                </div>
              )}
            </div>
          </div>

          {/* CUE CONTROL */}
          <div className="adm-module">
            <div className="adm-module-label">CUE CONTROL</div>
            <div className="adm-module-body">
              <div className="adm-cue-nav-row">
                <button className="adm-cue-nav-btn" onClick={handlePrevCue} disabled={liveIdx <= 0}>
                  <IconChevronLeft size={15} /> PREV
                </button>
                <button className="adm-cue-nav-btn adm-cue-nav-btn--next" onClick={handleNextCue} disabled={liveIdx >= sorted.length - 1}>
                  NEXT <IconChevronRight size={15} />
                </button>
              </div>

              {liveCue && (
                <div className="adm-live-readout">
                  <div className="adm-readout-row">
                    <span className="adm-readout-key">NOW</span>
                    <span className="adm-readout-val">{liveCue.cueNumber}. {liveCue.title}</span>
                  </div>
                  {liveElapsedSecs != null && (
                    <div className="adm-readout-row">
                      <span className="adm-readout-key">ELAPSED</span>
                      <span className="adm-readout-val adm-mono">{fmtCountdown(liveElapsedSecs)}</span>
                    </div>
                  )}
                </div>
              )}

              {nextCue && nextCueSecs != null && (
                <div className="adm-next-cue-block">
                  <div className="adm-next-label">NEXT UP</div>
                  <div className="adm-next-title">{nextCue.cueNumber}. {nextCue.title}</div>
                  <div className={`adm-next-countdown ${nextCueSecs < 60 ? 'adm-next-countdown--urgent' : ''}`}>
                    {nextCueSecs > 0 ? `in ${fmtCountdown(nextCueSecs)}` : `${fmtCountdown(Math.abs(nextCueSecs))} ago`}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* BROADCAST */}
          <div className="adm-module">
            <div className="adm-module-label">BROADCAST</div>
            <div className="adm-module-body">
              <textarea
                className="adm-broadcast-input"
                placeholder="Type a message to send to all viewers…"
                value={broadcastInput}
                onChange={e => setBroadcastInput(e.target.value)}
                rows={3}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendBroadcast(); } }}
              />
              <div className="adm-broadcast-actions">
                <button className="adm-broadcast-send" onClick={sendBroadcast} disabled={!broadcastInput.trim()}>
                  {broadcastSent ? '✓ Sent' : <><IconMegaphone size={15} /> Send</>}
                </button>
                <button className="adm-broadcast-clear" onClick={clearBroadcast}>
                  Clear
                </button>
              </div>
              <p className="adm-broadcast-hint">Expires 2 min after sending</p>
            </div>
          </div>

          {/* SHARE */}
          <div className="adm-module adm-module--share">
            <div className="adm-module-label">SHARE</div>
            <div className="adm-module-body">
              <label className="adm-share-toggle-row">
                <span className="adm-share-toggle-label">
                  {shareEnabled ? 'Live link is public' : 'Live link is off'}
                </span>
                <span
                  className={`adm-share-toggle${shareEnabled ? ' adm-share-toggle--on' : ''}`}
                  onClick={toggleShare}
                  role="switch"
                  aria-checked={shareEnabled}
                  aria-label="Public live link"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleShare(); } }}
                >
                  <span className="adm-share-toggle-knob" />
                </span>
              </label>
              {shareEnabled ? (
                <>
                  <div className="adm-url-display">{shareUrl}</div>
                  <button className="adm-copy-btn" onClick={copyUrl}>
                    {urlCopied ? '✓ COPIED' : 'COPY LINK'}
                  </button>
                  <button
                    className="adm-copy-btn adm-timer-btn"
                    onClick={() => window.open(`#/timer/${projectId}`, '_blank')}
                  >
                    <IconTimer size={15} /> OPEN SPEAKER TIMER
                  </button>
                  <p className="adm-share-hint">Anyone with these links can view the live cue sheet or speaker timer.</p>
                </>
              ) : (
                <p className="adm-share-hint">Sharing is off — only you and your team can view this sheet.</p>
              )}
            </div>
          </div>

        </aside>

        {/* ── Cue runway ── */}
        <div className="adm-runway-wrap">

          {/* Runway label */}
          <div className="adm-runway-header">
            <span className="adm-runway-label">CUE RUNWAY</span>
            <span className="adm-runway-count">{sorted.length} CUES</span>
          </div>

          <div className="adm-runway" ref={scrollRef}>
            {sorted.map((cue, index) => {
              const variant = cardVariant(index);
              const drift = driftMinutes(sorted, index, now.getTime());
              return (
                <div
                  key={cue.id}
                  ref={cue.isLive ? liveCardRef : undefined}
                  className={`adm-card adm-card--${variant}`}
                >
                  {/* Status strip */}
                  <div className={`adm-card-strip adm-card-strip--${variant}`} />

                  {/* Card header */}
                  <div className="adm-card-head">
                    <span className={`adm-num adm-num--${variant}`}>{cue.cueNumber}</span>
                    <div className="adm-card-title-wrap">
                      <span className="adm-card-title">{cue.title}</span>
                      {variant === 'live' && <span className="adm-live-badge">● LIVE</span>}
                      {variant === 'next' && <span className="adm-next-badge">NEXT</span>}
                    </div>
                    {drift !== null && (
                      <span className={`adm-drift-pill adm-drift--${drift === 0 ? 'ok' : drift > 0 ? 'late' : 'early'}`}>
                        {drift === 0 ? '✓' : drift > 0 ? `+${drift}m` : `${drift}m`}
                      </span>
                    )}
                    <button className="adm-card-edit-btn" onClick={() => openEditor(cue)} title="Edit cue" aria-label={`Edit cue ${cue.cueNumber}`}><IconEdit size={15} /></button>
                  </div>

                  {/* Times */}
                  <div className="adm-card-times">
                    <span className="adm-mono adm-time-val">{fmtTime(cue.startTime)}</span>
                    <span className="adm-time-sep">→</span>
                    <span className="adm-mono adm-time-val">{fmtTime(cue.endTime)}</span>
                    <span className="adm-dur">{fmtDuration(cue.startTime, cue.endTime)}</span>
                  </div>

                  {/* Live progress bar */}
                  {variant === 'live' && (
                    <div className="adm-card-progress">
                      <div className="adm-card-progress-fill" style={{ width: `${liveProgressPct}%` }} />
                    </div>
                  )}

                  {cue.actualStartTime && (
                    <div className="adm-actual-start">
                      actual {fmtTime(cue.actualStartTime)}
                    </div>
                  )}

                  {/* Fields */}
                  <div className="adm-card-fields">
                    {fields.map(f => (
                      <div key={f.id} className="adm-field-row">
                        <span className="adm-f-key">{f.label}</span>
                        <span className="adm-f-val">{cue.fieldValues[f.id] || <span className="adm-f-empty">—</span>}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {sorted.length === 0 && (
              <div className="adm-empty">NO CUES LOADED</div>
            )}
          </div>
        </div>
      </div>
      {/* ── Cue editor drawer ── */}
      {editingCue && (
        <>
          <div className="adm-drawer-overlay" onClick={() => setEditingCue(null)} />
          <div className="adm-drawer">
            <div className="adm-drawer-header">
              <div>
                <span className="adm-drawer-label">EDITING CUE {editingCue.cueNumber}</span>
                <h2 className="adm-drawer-title">{editingCue.title}</h2>
              </div>
              <button className="adm-drawer-close" onClick={() => setEditingCue(null)} aria-label="Close editor"><IconX size={18} /></button>
            </div>

            <div className="adm-drawer-body">

              {/* Title */}
              <div className="adm-editor-field">
                <label className="adm-editor-label">Title</label>
                <input
                  className="adm-editor-input"
                  value={editForm.title}
                  onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                />
              </div>

              {/* Times */}
              <div className="adm-editor-row">
                <div className="adm-editor-field">
                  <label className="adm-editor-label">Start time</label>
                  <input
                    className="adm-editor-input"
                    type="time"
                    value={editForm.startTime}
                    onChange={e => setEditForm(f => ({ ...f, startTime: e.target.value }))}
                  />
                </div>
                <div className="adm-editor-field">
                  <label className="adm-editor-label">End time</label>
                  <input
                    className="adm-editor-input"
                    type="time"
                    value={editForm.endTime}
                    onChange={e => setEditForm(f => ({ ...f, endTime: e.target.value }))}
                  />
                </div>
              </div>

              {/* Custom fields */}
              <div className="adm-editor-section-label">Fields</div>
              {fields.map(f => (
                <div key={f.id} className="adm-editor-field">
                  <label className="adm-editor-label">{f.label}</label>
                  <input
                    className="adm-editor-input"
                    value={editForm.fieldValues[f.id] || ''}
                    onChange={e => setEditForm(prev => ({
                      ...prev,
                      fieldValues: { ...prev.fieldValues, [f.id]: e.target.value },
                    }))}
                    placeholder="—"
                  />
                </div>
              ))}
            </div>

            <div className="adm-drawer-footer">
              <button className="adm-drawer-cancel" onClick={() => setEditingCue(null)}>Cancel</button>
              <button className="adm-drawer-save" onClick={saveEdit}>Save changes</button>
            </div>
          </div>
        </>
      )}

      {/* Print / PDF (hidden on screen; shown by the print stylesheet) */}
      {project && (
        <PrintableCueSheet
          title={project.title}
          date={project.date instanceof Date ? project.date : new Date(project.date)}
          cues={cues}
          fields={fields}
        />
      )}
    </div>
  );
}

export default AdminPage;
